import React, { useMemo, useState } from "react";
import { IconButton, Tooltip, Typography, Box } from "@mui/material";
import { List as ListIcon } from "@mui/icons-material";
import { observer } from "mobx-react";
import moment from "moment";
import useStore from "../../../helpers/useStore";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
import { useAppointmentsByPatient } from "../../../stores/Appointment/appointment.queries";
import { AppointmentCardView } from "../../../components/PatientOverview/RxInfo/TerminView";
import { TerminViewConstants } from "../../../components/PatientOverview/TerminView";
import { AppointmentDto } from "../../../api-client";
import { DrawerCard, DrawerCardSpinner } from "./DrawerCard";
import AppointmentOverviewModal from "../../../components/PatientOverview/RxInfo/AppointmentOverviewModal";
import {
  WeekContextStrip,
  WeekRow,
  appointmentDayStatus,
} from "./WeekContextStrip";

interface ScheduleContextProps {
  patientId: number;
}

/**
 * Mo–Fr, like the calendar itself. Weekend appointments do not appear here —
 * that matches the previous implementation, which iterated the same five days.
 */
const WEEKDAY_COUNT = 5;

/**
 * Height reserved for the spinner. The populated card is a fixed-size strip
 * plus a handful of appointment cards, so a placeholder near that keeps the
 * drawer from collapsing and re-expanding around every patient load.
 */
const LOADING_MIN_HEIGHT = 240;

/**
 * The patient's appointment context: the week being viewed, flanked by the
 * week before and the week after.
 *
 * The arrangement lives in the `WeekContextStrip` mini-calendar; the cards
 * below it carry the detail for the days that have appointments, in date
 * order. The strip replaced the per-week headers, the "Di 28.07. – Do 30.07."
 * empty-range rows and the day labels above each card — every card already
 * states its own date, so all three were spending height on what the grid now
 * shows positionally.
 */
const ScheduleContext: React.FC<ScheduleContextProps> = observer(({ patientId }) => {
  const calendarStore = useStore(CalendarContext);
  const selectedDateStr = calendarStore?.selectedDateStr;
  const selectedDate = moment(selectedDateStr);
  const selectedDateKey = selectedDate.format("YYYY-MM-DD");

  const { data: appointments, isLoading, error } = useAppointmentsByPatient(patientId);
  const [allAppointmentsOpen, setAllAppointmentsOpen] = useState(false);

  // The strip deliberately shows only three weeks. This is the way out to
  // everything — the same modal the calendar's "Terminübersicht" context item opens.
  const allAppointmentsAction = (
    <Tooltip title="Terminübersicht">
      <IconButton
        aria-label="Terminübersicht"
        onClick={() => setAllAppointmentsOpen(true)}
        sx={{ padding: 0.5, "& .MuiSvgIcon-root": { fontSize: 18 } }}
      >
        <ListIcon />
      </IconButton>
    </Tooltip>
  );

  const weekStarts = useMemo(() => {
    const viewedWeekStart = selectedDate.clone().startOf("isoWeek");
    return [
      viewedWeekStart.clone().subtract(1, "week"),
      viewedWeekStart,
      viewedWeekStart.clone().add(1, "week"),
    ];
  }, [selectedDateKey]);

  // Group appointments by date
  const appointmentsByDate = useMemo(() => {
    const map = new Map<string, AppointmentDto[]>();
    if (appointments) {
      appointments.forEach((apt) => {
        if (apt.start) {
          const dateKey = moment(apt.start).format("YYYY-MM-DD");
          const existing = map.get(dateKey) || [];
          existing.push(apt);
          map.set(dateKey, existing);
        }
      });
    }
    return map;
  }, [appointments]);

  if (isLoading) {
    return (
      <DrawerCard title="Terminkontext" action={allAppointmentsAction}>
        <DrawerCardSpinner minHeight={LOADING_MIN_HEIGHT} />
      </DrawerCard>
    );
  }

  if (error) {
    // No reserved height here — an error is terminal, so holding 240px of
    // blank space open below one line of text would be worse than the jump.
    return (
      <DrawerCard title="Terminkontext" action={allAppointmentsAction}>
        <Typography color="error">Fehler beim Laden der Termine</Typography>
      </DrawerCard>
    );
  }

  const days = weekStarts.map((weekStart) =>
    Array.from({ length: WEEKDAY_COUNT }, (_, i) => weekStart.clone().add(i, "days"))
  );

  const weeks: WeekRow[] = days.map((weekDays, w) => ({
    isoWeek: weekStarts[w].isoWeek(),
    // weekStarts is [previous, viewed, next], so the middle row is the week
    // being looked at. It gets the band.
    isViewedWeek: w === 1,
    days: weekDays.map((day) => {
      const dateKey = day.format("YYYY-MM-DD");
      return {
        dateKey,
        dayOfMonth: day.date(),
        label: day.format("dd DD.MM.YYYY"),
        appointments: (appointmentsByDate.get(dateKey) || []).map(appointmentDayStatus),
      };
    }),
  }));

  // A week's cards, in day order, keyed by its ISO week for renderWeekContent.
  const appointmentsByWeek = new Map<number, AppointmentDto[]>(
    days.map((weekDays, w) => [
      weekStarts[w].isoWeek(),
      weekDays.flatMap((day) => appointmentsByDate.get(day.format("YYYY-MM-DD")) || []),
    ])
  );

  return (
    <DrawerCard title="Terminkontext" action={allAppointmentsAction}>
      <WeekContextStrip
        weeks={weeks}
        selectedDateKey={selectedDateKey}
        onSelectDate={(dateKey) => calendarStore?.setSelectedDateStr(dateKey)}
        renderWeekContent={(week) => {
          const weekAppointments = appointmentsByWeek.get(week.isoWeek) || [];
          if (weekAppointments.length === 0) return null;
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {weekAppointments.map((apt) => (
                <AppointmentCardView
                  key={apt.id}
                  appointment={apt}
                  appointments={appointments || []}
                  patientId={patientId}
                  isView={TerminViewConstants.TERMIN_VIEW}
                />
              ))}
            </Box>
          );
        }}
      />
      {allAppointmentsOpen && (
        <AppointmentOverviewModal
          open={allAppointmentsOpen}
          onClose={() => setAllAppointmentsOpen(false)}
          patientId={patientId}
        />
      )}
    </DrawerCard>
  );
});

export default ScheduleContext;
